import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormWrapper} from "../items/form.wrapper";

import {ReactiveFormsModule} from "@angular/forms";
import {TranslatePipe} from "@ngx-translate/core";
import {FormSelectOption} from "../items/form-select-option";
import {FormFieldErrorComponent} from "../form-field-error/form-field-error.component";
import {FormLabelComponent} from "../form-label/form-label.component";
import {isDefined, isFalse, isTrue, nonBlank} from "../../utils/epic.utils";
import {IconComponent} from "../../icon/icon.component";
import {NgbPopover, NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {FormSelectConfig} from "./form-select.config";
import {AccClickOutsideDirective} from "../../utils/acc-click-outside.directive";
import {Size} from "../../../base/data/size";
import {AbstractFormSelectFieldComponent} from "../abstract-form-select-field.component";
import {FormRadiosConfig} from "../form-radios/form-radios.config";
import {FormMetadata} from "../items/form-metadata";

@Component({
  selector: 'acc-form-select',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    FormFieldErrorComponent,
    FormLabelComponent,
    IconComponent,
    NgbPopover,
    NgbTooltip,
    AccClickOutsideDirective
  ],
  templateUrl: './form-select.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-select.component.css'
})
export class FormSelectComponent extends AbstractFormSelectFieldComponent implements OnInit, OnDestroy {

  @ViewChild('tooltip')
  public tooltip: NgbTooltip | undefined;

  @Input({required: true})
  public name!: string;

  @Input({required: true})
  public wrapper!: FormWrapper;

  @Input()
  public config: FormSelectConfig = new FormSelectConfig();

  @Output()
  public accChanged: EventEmitter<FormSelectOption | undefined> = new EventEmitter<FormSelectOption | undefined>();

  protected showPlaceholder: boolean = true;

  protected override accOnInit(): void {
    this.subs.push(this.getWrapper().onReady().subscribe(() => {
      const metadata: FormMetadata | undefined = this.getWrapper().getMetadataByName(this.getName());
      if (isDefined(metadata)) {
        this.subs.push(metadata.onChanged().subscribe(() => {
          this.subs.push(metadata.loadSelectOptions().subscribe(() => {
            this.fieldOptionsInit(true);
          }));
        }));
      } else {
        this.log.error('Metadata not found for form "{}" and field "{}"', [this.getWrapper().getId(), this.getName()]);
      }
    }));
  }

  protected override getName(): string {
    return this.name;
  }

  protected override getWrapper(): FormWrapper {
    return this.wrapper;
  }

  protected override getConfig(): FormSelectConfig | FormRadiosConfig {
    return this.config;
  }

  protected override getTooltip(): NgbTooltip | undefined {
    return this.tooltip;
  }

  protected override isViewOnly(): boolean {
    return this._isViewOnly(this.name, this.wrapper);
  }

  protected override getSize(): Size | undefined {
    return this.config.size;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  protected override afterValueChanged(_name: string, newValue: any, _oldValue: any, initialValue: boolean): void {
    if (!initialValue) {
      const option: FormSelectOption | undefined = this.selectOptions.find((o) => o.value === newValue);
      this.accChanged.next(option);
      this.updateSelectedByOption(option);
    }
  }

  protected isRequiredField(): boolean {
    return this._isRequiredField(this.name, this.wrapper);
  }

  protected isShowSelectionPrompt(): boolean {
    if (isDefined(this.config.showSelectionPrompt)) {
      return this.config.showSelectionPrompt;
    }
    return this.isRequiredField();
  }

  protected isOptionSelected(option: FormSelectOption): boolean {
    if (this.wrapper.isReady) {
      return option.value === this.wrapper.getValue(this.name);
    }
    return false;
  }

  protected onSelectChange(event: Event): void {
    this._onChange(this.name, this.wrapper, event);
  }

  protected isInvalid(): boolean {
    return this._isInvalid(this.name, this.wrapper);
  }

  protected isPlaceholder(): boolean {
    return this.showPlaceholder && !this.hasSelectedOptions();
  }

  protected cleanValue(): void {
    const oldValue: any = this.wrapper.getControl(this.name)?.value;
    this._onChangeValue(this.name, this.wrapper, '', oldValue);
  }

  protected showCleanAction(): boolean {
    if (isTrue(this.config.showCleanAction)) {
      return true;
    } else if (isFalse(this.config.showCleanAction)) {
      return false;
    }
    const value: any = this.wrapper.getControl(this.name)?.value;
    const required: boolean = this._isRequiredField(this.name, this.wrapper);
    return nonBlank(value) && !required && !this.isViewOnly();
  }

  protected getEditClass(): string {
    return isDefined(this.config.size) ? 'form-select-' + this.config.size.value : '';
  }

  protected getViewClass(): string {
    return isDefined(this.config.size) ? 'form-control-' + this.config.size.value : '';
  }
}
